import React, { useEffect, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Stack,
  Button,
} from "@mui/material";
import {
  useGetTicketsToValidateList,
  useGetTicketsValidationCount,
} from "src/api/tickets";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TableActions from "./table-actions";
import { useGetLocationsList } from "src/api/locations";
import ScanPopup from "../scan-popup";

const tableHeadText = [
  "Ticket Number",
  "Location",
  "Date",
  "Check-in Time",
  "Requested Time",
  "Total Hours",
  "Value",
  "Actions",
];

const SearchValidateTable = ({
  setForceRefetch,
  forceRefetch,
}: {
  setForceRefetch: Function;
  forceRefetch: boolean;
}) => {
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [searchBy, setSearchBy] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  const filters = {
    ...(searchBy && { search: searchBy }),
  };

  // APIS
  const {
    data: transactionsData,
    isFetching,
    status,
    error,
    refetch,
  } = useGetTicketsToValidateList(filters);
  const { data: locationsData } = useGetLocationsList({ isClient: true });
  const { data: countData, refetch: refetchCount } =
    useGetTicketsValidationCount({
      locationId: locationsData?.locations?.[0]?.id ?? undefined,
    });

  const handleOpenPopup = () => {
    setOpen(true);
  };
  const handleClosePopup = () => {
    setOpen(false);
  };

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const clearSearch = () => {
    if (searchBy !== "") {
      setSearchBy("");
      handleSearch("");
    }
  };

  const getScannerCode = (code: string) => {
    setSearchBy(code);
  };

  const emptyDataArr = transactionsData?.data?.length === 0;

  useEffect(() => {
    refetch();
    refetchCount();
  }, [forceRefetch]);

  // error handling
  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent={"space-between"}
        gap={1}
        mb={3}
      >
        <Box>
          <Stack direction={"row"} gap={1.5}>
            <TextField
              size="small"
              placeholder="Search"
              value={tempSearchBy}
              onChange={(event) => handleSearch(event.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearchBy(tempSearchBy);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton onClick={() => setSearchBy(tempSearchBy)}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                  >
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button onClick={handleOpenPopup}>Scan Ticket</Button>
            {/* <QRScanner /> */}
          </Stack>

          <Typography color={"#A1A5B7"} fontSize={"12px"} mt={1}>
            You can search by Ticket Number, Ex: 2025121803261829, or Plate
            Number, Ex: ART1212, or Mobile Number, Ex: 966555555555
          </Typography>
        </Box>
        <Typography fontSize={"16px"}>
          You validated {countData?.data?.count} tickets
        </Typography>
      </Box>

      {!searchBy ? (
        <Paper
          sx={{
            bgcolor: "#1A1A1A",
            boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
            padding: { xs: "15px", md: "30px" },
            height: "200px",
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeadText.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>{" "}
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper
          sx={{
            bgcolor: "#1A1A1A",
            boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
            padding: { xs: "15px", md: "30px" },
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeadText.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isFetching ? (
                <TableLoadingSkeleton rowsLength={2} cellsLength={8} />
              ) : (
                <TableBody>
                  {transactionsData?.data?.map((row: any) => {
                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          cursor: "pointer",
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.ticketNumber}</TableCell>
                        <TableCell>{row.location}</TableCell>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row?.checkInTime}</TableCell>
                        <TableCell>{row.requestedTime}</TableCell>
                        <TableCell>{row.totalHours}</TableCell>
                        <TableCell>{row.value}</TableCell>
                        <TableCell>
                          <TableActions
                            rowId={row.id}
                            ticketNumber={row.ticketNumber}
                            setForceRefetch={setForceRefetch}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>

            {emptyDataArr && <NoData />}
          </TableContainer>
        </Paper>
      )}
      <ScanPopup
        open={open}
        handleClose={handleClosePopup}
        onSave={getScannerCode}
      />
    </>
  );
};
export default SearchValidateTable;
